import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ArticleView from "@/pages/learn/components/ArticleView";
import TableOfContents from "@/pages/learn/components/TableOfContents";
import NotFound from "@/pages/NotFound";
import { learnArticles, learnIndexCards } from "@/mocks/learn";
import { learnSubGuides } from "@/mocks/learnSubGuides";
import { texasCommunityCollegeArticle } from "@/mocks/learnTexasCommunityCollege";
import { englishTestArticle } from "@/mocks/learnEnglishTest";
import type { LearnArticle } from "@/pages/learn/types";

const articles = [
  ...(learnArticles as unknown as LearnArticle[]),
  texasCommunityCollegeArticle as unknown as LearnArticle,
  englishTestArticle as unknown as LearnArticle,
];

const subGuidesOf = (slug: string) => learnSubGuides.filter((guide) => guide.parentSlug === slug);

export default function LearnDetail() {
  const { slug } = useParams();
  const article = articles.find((item) => item.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const relatedSubGuides = subGuidesOf(article.slug);
  const headings: { id: string; text: string }[] = [];
  article.blocks.forEach((block) => {
    if (block.kind === "h") {
      headings.push({ id: `section-${headings.length + 1}`, text: block.text });
    }
  });
  const others = articles
    .filter((item) => !item.parentSlug && item.slug !== article.slug)
    .slice(0, 3);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <div className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-content px-5 py-10 md:px-6 md:py-14">
            <nav aria-label="パンくずリスト" className="flex flex-wrap items-center gap-2 text-[11.5px] text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-700">
                ホーム
              </Link>
              <i className="ri-arrow-right-s-line text-[13px]" />
              <Link to="/learn" className="transition-colors hover:text-primary-700">
                無料で学ぶ
              </Link>
              <i className="ri-arrow-right-s-line text-[13px]" />
              {article.parentSlug ? (
                <>
                  <Link
                    to={`/learn/${article.parentSlug}`}
                    className="transition-colors hover:text-primary-700"
                  >
                    {article.parentLabel ?? article.category}
                  </Link>
                  <i className="ri-arrow-right-s-line text-[13px]" />
                  <span className="text-foreground-700">{article.title}</span>
                </>
              ) : (
                <span className="text-foreground-700">{article.category}</span>
              )}
            </nav>

            <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-100 px-3.5 py-1 text-[11px] font-semibold text-primary-800">
              <i className="ri-book-open-line text-[12px]" />
              無料記事
            </p>
            <h1 className="mt-4 font-heading text-[27px] font-bold leading-[1.5] tracking-tight text-foreground-950 md:text-[36px]">
              {article.title}
            </h1>
            <p className="mt-4 max-w-2xl text-[13px] leading-[2] text-foreground-700 md:text-[14px]">
              {article.lead}
            </p>
            <p className="mt-5 flex items-center gap-2 text-[11.5px] text-foreground-500">
              <i className="ri-refresh-line text-[13px] text-primary-600" />
              最終更新：{article.updatedAt}
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-content px-5 py-12 md:px-6 md:py-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_290px] lg:gap-12">
            <article className="min-w-0">
              <TableOfContents items={headings} />
              <ArticleView blocks={article.blocks} />

              {relatedSubGuides.length > 0 && (
                <div className="mt-10">
                  <h2 className="flex items-center gap-2 font-heading text-[15px] font-bold text-foreground-950">
                    <i className="ri-bookmark-line text-[16px] text-primary-600" />
                    この記事の詳細ページ
                  </h2>
                  <div className="mt-4 grid gap-3.5 sm:grid-cols-2">
                    {relatedSubGuides.map((guide) => (
                      <Link
                        key={guide.slug}
                        to={`/learn/${guide.slug}`}
                        className="group flex h-full flex-col rounded-[10px] border border-background-200 bg-background-50 p-5 transition-all duration-300 hover:-translate-y-[3px] hover:border-primary-300 hover:shadow-[0_16px_32px_-24px_rgba(23,37,35,0.4)]"
                      >
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-700">
                          <i className={`${guide.icon} text-[18px]`} />
                        </span>
                        <p className="mt-3.5 font-heading text-[14px] font-bold leading-[1.6] text-foreground-950">
                          {guide.title}
                        </p>
                        <p className="mt-2 flex-1 text-[12px] leading-[1.85] text-foreground-600">
                          {guide.description}
                        </p>
                        <span className="mt-3.5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary-700">
                          読む
                          <i className="ri-arrow-right-line text-[13px] transition-transform duration-300 group-hover:translate-x-0.5" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-12 rounded-[12px] border border-background-200 bg-background-100 px-6 py-7">
                <p className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-3 py-1 text-[11px] font-semibold text-foreground-600">
                  <i className="ri-lock-line text-[12px]" />
                  有料ガイド（準備中）
                </p>
                <h2 className="mt-4 font-heading text-[17px] font-bold text-foreground-950">
                  実践的な資料はPDFガイドで
                </h2>
                <p className="mt-2.5 text-[12.5px] leading-[1.95] text-foreground-600">
                  無料記事では制度の基本をお伝えしています。有料PDFガイドでは、オリジナルのチェックリストや学習資料、学校比較テンプレートなどの追加価値をまとめる予定です。現在は制作中のため購入できません。
                </p>
                <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
                  <Link
                    to="/guides"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-700 px-5 py-2.5 text-[12.5px] font-semibold text-background-50 transition-colors hover:bg-primary-800"
                  >
                    有料ガイド一覧を見る
                    <i className="ri-arrow-right-line text-[14px]" />
                  </Link>
                  <Link
                    to="/learn"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-[12.5px] font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-800"
                  >
                    無料記事一覧に戻る
                  </Link>
                </div>
              </div>
            </article>

            <aside className="w-full lg:sticky lg:top-[112px] lg:self-start">
              <div className="rounded-[10px] border border-background-200 bg-background-50 p-5">
                <p className="font-heading text-[13px] font-bold text-foreground-950">無料記事一覧</p>
                <ul className="mt-3.5 space-y-1">
                  {learnIndexCards.map((card) => {
                    const active = card.slug === article.slug;
                    const subs = subGuidesOf(card.slug);
                    return (
                      <li key={card.slug}>
                        <Link
                          to={`/learn/${card.slug}`}
                          className={`flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-[12.5px] transition-colors ${
                            active
                              ? "bg-primary-50 font-semibold text-primary-800"
                              : "text-foreground-700 hover:bg-background-100 hover:text-primary-800"
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="font-heading text-[10.5px] font-bold text-background-400">
                              {card.number}
                            </span>
                            {card.title}
                          </span>
                          {active && <i className="ri-arrow-right-line text-[12px]" />}
                        </Link>
                        {subs.length > 0 && (
                          <ul className="mb-1 ml-3 mt-1 space-y-1 border-l border-background-200 pl-2">
                            {subs.map((sub) => {
                              const subActive = sub.slug === article.slug;
                              return (
                                <li key={sub.slug}>
                                  <Link
                                    to={`/learn/${sub.slug}`}
                                    className={`flex items-center justify-between gap-1.5 rounded-md px-2 py-1.5 text-[11.5px] transition-colors ${
                                      subActive
                                        ? "bg-primary-50 font-semibold text-primary-800"
                                        : "text-foreground-600 hover:bg-background-100 hover:text-primary-800"
                                    }`}
                                  >
                                    <span className="flex items-center gap-1.5">
                                      <i className={`${sub.icon} text-[12px]`} />
                                      {sub.title}
                                    </span>
                                    {subActive && <i className="ri-arrow-right-line text-[11px]" />}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="mt-4 rounded-[10px] border border-background-200 bg-background-100 p-5">
                <p className="font-heading text-[13px] font-bold text-foreground-950">留学ブログ</p>
                <p className="mt-2 text-[12px] leading-[1.85] text-foreground-600">
                  日々の記録はnoteで更新しています。
                </p>
                <a
                  href="https://note.com/mypace_diary"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3.5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-accent-700 transition-colors hover:text-accent-800"
                >
                  noteを読む
                  <i className="ri-external-link-line text-[12px]" />
                </a>
              </div>
            </aside>
          </div>

          <div className="mt-14 border-t border-background-200 pt-10">
            <h2 className="font-heading text-[18px] font-bold text-foreground-950 md:text-[20px]">
              あわせて読みたい無料記事
            </h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3 md:gap-5">
              {others.map((item) => (
                <Link
                  key={item.slug}
                  to={`/learn/${item.slug}`}
                  className="group rounded-[10px] border border-background-200 bg-background-50 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 hover:shadow-[0_16px_32px_-24px_rgba(23,37,35,0.4)]"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-700">
                    <i className={`${item.icon} text-[18px]`} />
                  </span>
                  <h3 className="mt-4 font-heading text-[14.5px] font-bold leading-[1.6] text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-[12px] leading-[1.85] text-foreground-600">
                    {item.lead}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}